import React, { useEffect, useRef } from 'react';
import { array, bool, func, string, object } from 'prop-types';
import { FormattedMessage } from '../../../util/reactIntl';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { ListingCard } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { propTypes } from '../../../util/types';
import { fetchSimilarProducts } from './SimilarProducts.duck';

import css from './SimilarProducts.module.css';

/**
 * SimilarProductsComponent displays similar products at the bottom of a listing page.
 */
const SimilarProductsComponent = props => {
  const config = useConfiguration();
  const lastFetchedListingId = useRef(null);
  const lastFetchedCategory = useRef(null);
  const lastFetchTimestamp = useRef(0);
  // Re-randomize similar products every 10 minutes (in milliseconds)
  const REFRESH_INTERVAL = 10 * 60 * 1000;

  const {
    className,
    rootClassName,
    currentListing,
    currentUser,
    onToggleFavorites,
    similarProducts,
    similarProductsInProgress,
    onFetchSimilarProducts,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // Fetch similar products when the listing changes
  useEffect(() => {
    // Make sure we have a valid listing with an ID
    if (!currentListing?.id?.uuid) {
      return;
    }

    // Get the listing ID as a string
    const listingId = currentListing.id.uuid;

    // Get the category
    const categoryLevel1 =
      currentListing.attributes?.publicData?.categoryLevel1;

    // Check if we need to fetch new data
    const isSameListing = lastFetchedListingId.current === listingId;
    const isSameCategory = lastFetchedCategory.current === categoryLevel1;
    const currentTime = Date.now();
    const timeSinceLastFetch = currentTime - lastFetchTimestamp.current;

    // Skip if we're dealing with the same listing and same category
    // AND we haven't exceeded our refresh interval
    if (
      isSameListing &&
      isSameCategory &&
      timeSinceLastFetch < REFRESH_INTERVAL
    ) {
      return;
    }

    // Update tracking refs
    lastFetchedListingId.current = listingId;
    lastFetchedCategory.current = categoryLevel1;
    lastFetchTimestamp.current = currentTime;

    // Clear previous similar products if this listing has no category
    if (!categoryLevel1) {
      onFetchSimilarProducts(currentListing.id, null, config);
      return;
    }

    // Fetch similar products when category is available
    onFetchSimilarProducts(currentListing.id, categoryLevel1, config);
  }, [
    currentListing?.id?.uuid,
    currentListing?.attributes?.publicData?.categoryLevel1,
    onFetchSimilarProducts,
    config,
  ]);

  // Don't render anything if no results and loading is complete
  if (
    !similarProductsInProgress &&
    (!similarProducts || similarProducts.length === 0)
  ) {
    return null;
  }

  // Card rendering sizes
  const cardRenderSizes = [
    '(max-width: 549px) 50vw',
    '(max-width: 767px) 50vw',
    '(max-width: 1023px) 33vw',
    '(max-width: 1439px) 25vw',
    '20vw',
  ].join(', ');

  return (
    <div className={classes}>
      <h2 className={css.title}>
        <FormattedMessage
          id="SimilarProducts.title"
          defaultMessage="You might also like"
        />
      </h2>

      {similarProductsInProgress ? (
        <div className={css.loader}>
          <FormattedMessage
            id="SimilarProducts.loading"
            defaultMessage="Loading similar products..."
          />
        </div>
      ) : (
        <div className={css.listings}>
          {similarProducts.map(listing => (
            <ListingCard
              className={css.listingCard}
              key={listing.id.uuid}
              listing={listing}
              renderSizes={cardRenderSizes}
              onToggleFavorites={null}
              currentUser={currentUser}
            />
          ))}
        </div>
      )}
    </div>
  );
};

SimilarProductsComponent.defaultProps = {
  className: null,
  rootClassName: null,
  onToggleFavorites: null,
  currentUser: null,
  similarProducts: [],
  similarProductsInProgress: false,
  similarProductsError: null,
};

SimilarProductsComponent.propTypes = {
  className: string,
  rootClassName: string,
  currentListing: propTypes.listing,
  onToggleFavorites: func,
  currentUser: propTypes.currentUser,
  similarProducts: array,
  similarProductsInProgress: bool,
  similarProductsError: object,
  onFetchSimilarProducts: func.isRequired,
};

// Configure props from Redux store
const mapStateToProps = state => {
  const similarProductsState = state.SimilarProducts || {};
  return {
    similarProducts: similarProductsState.similarProducts || [],
    similarProductsInProgress:
      similarProductsState.similarProductsInProgress || false,
    similarProductsError: similarProductsState.similarProductsError || null,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchSimilarProducts: (listingId, categoryLevel1, config) => {
    return dispatch(fetchSimilarProducts(listingId, categoryLevel1, config));
  },
});

// Connect the component to Redux store
const SimilarProducts = compose(connect(mapStateToProps, mapDispatchToProps))(
  SimilarProductsComponent
);

// Export the connected component
export default SimilarProducts;
